import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "影视作品", to: "/works" },
  { label: "动态", to: "/news" },
  { label: "个人资料", to: "/profile" },
  { label: "关于", to: "/about" },
];

interface SiteHeaderProps {
  overlay?: boolean;
}

export default function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const transparent = overlay && !scrolled && !mobileOpen;

  return (
    <header
      className={[
        "fixed top-0 left-0 w-full z-50 transition-all duration-300",
        transparent
          ? "bg-transparent"
          : "bg-background-50/95 backdrop-blur border-b border-background-200",
      ].join(" ")}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2.5 group">
            <span
              className={[
                "w-9 h-9 flex items-center justify-center rounded-md transition-colors",
                transparent ? "bg-white/15" : "bg-primary-500",
              ].join(" ")}
            >
              <i className={`ri-movie-2-line text-lg ${transparent ? "text-white" : "text-background-50"}`}></i>
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={[
                  "font-heading text-lg md:text-xl font-bold tracking-wide",
                  transparent ? "text-white" : "text-foreground-950",
                ].join(" ")}
              >
                奥田咲
              </span>
              <span
                className={[
                  "text-[10px] tracking-[0.28em] mt-1 hidden sm:block",
                  transparent ? "text-white/70" : "text-foreground-500",
                ].join(" ")}
              >
                SAKI OKUDA
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  [
                    "px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap",
                    isActive
                      ? transparent
                        ? "text-white bg-white/15"
                        : "text-primary-700 bg-primary-100"
                      : transparent
                        ? "text-white/80 hover:text-white hover:bg-white/10"
                        : "text-foreground-700 hover:text-primary-700 hover:bg-background-100",
                  ].join(" ")
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/works"
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              浏览作品
              <i className="ri-arrow-right-line"></i>
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="打开菜单"
              className={[
                "md:hidden w-10 h-10 flex items-center justify-center rounded-md transition-colors cursor-pointer",
                transparent ? "text-white hover:bg-white/10" : "text-foreground-900 hover:bg-background-100",
              ].join(" ")}
            >
              <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-3-line text-2xl"}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 animate-fade-in">
          <nav className="px-4 py-3 flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  [
                    "flex items-center justify-between px-3 py-3.5 rounded-md text-base transition-colors",
                    isActive
                      ? "text-primary-700 bg-primary-100 font-semibold"
                      : "text-foreground-800 hover:bg-background-100",
                  ].join(" ")
                }
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </NavLink>
            ))}
            <Link
              to="/works"
              className="mt-2 flex items-center justify-center gap-1.5 px-4 py-3 rounded-md bg-accent-500 text-background-50 text-base font-medium"
            >
              浏览全部作品
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}